import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Search, Plus, RefreshCw, Trash2, Eye, Pencil, Loader2, Copy } from "lucide-react";
import { api, faDigits, faNum, timeAgo } from "../lib/api";
import { copyText } from "../lib/copy";

export default function Accounts() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [cookieModal, setCookieModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [refreshing, setRefreshing] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/accounts", { params: { search, status } });
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error("خطا در بارگذاری اکانت‌ها");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const toggleEnabled = async (a) => {
    try {
      await api.patch(`/admin/accounts/${a.id}/enabled`, { enabled: !a.enabled });
      toast.success(!a.enabled ? "اکانت فعال شد" : "اکانت غیرفعال شد");
      load();
    } catch { toast.error("خطا در تغییر وضعیت"); }
  };
  const toggleRefresh = async (a) => {
    try {
      await api.patch(`/admin/accounts/${a.id}/refresh-disabled`, { refresh_disabled: !a.refresh_disabled });
      toast.success(!a.refresh_disabled ? "رفرش این اکانت غیرفعال شد" : "رفرش این اکانت فعال شد");
      load();
    } catch { toast.error("خطا"); }
  };
  const doRefresh = async (a) => {
    setRefreshing((r) => ({ ...r, [a.id]: true }));
    try {
      const { data } = await api.post(`/admin/accounts/${a.id}/refresh`);
      if (data.success) toast.success(`رفرش موفق در ${faNum(data.duration_ms)} میلی‌ثانیه`);
      else toast.error(data.message || "رفرش ناموفق");
      load();
    } catch { toast.error("خطا در رفرش"); }
    finally { setRefreshing((r) => ({ ...r, [a.id]: false })); }
  };
  const doDelete = async (a) => {
    if (!window.confirm(`حذف اکانت ${a.id}؟`)) return;
    try { await api.delete(`/admin/accounts/${a.id}`); toast.success("حذف شد"); load(); }
    catch { toast.error("خطا"); }
  };
  const showCookie = async (a) => {
    try {
      const { data } = await api.get(`/admin/accounts/${a.id}/cookie`);
      setCookieModal({ account: a, cookie: data.cookie, sana: data.sana_cookie });
    } catch { toast.error("خطا"); }
  };

  return (
    <div data-testid="accounts-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">مدیریت اکانت‌ها</h1>
          <p className="text-sm text-slate-500 mt-1">جستجو، فیلتر، ویرایش، غیرفعال‌سازی و رفرش اکانت‌ها</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="refresh-all-button"
            onClick={async () => { toast.info("در حال رفرش همه..."); await api.post("/admin/accounts/refresh-all"); toast.success("انجام شد"); load(); }}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all"
          ><RefreshCw size={16} /> رفرش همه</button>
          <button
            data-testid="add-account-button"
            onClick={() => nav("/accounts/add")}
            className="bg-teal-600 hover:bg-teal-700 text-white font-medium px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all shadow-sm active:scale-[0.98]"
          ><Plus size={16} /> افزودن اکانت</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            data-testid="accounts-search-input"
            type="text" placeholder="جستجو بر اساس کد ملی..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
          />
        </div>
        <select
          data-testid="accounts-status-filter"
          value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-teal-500 bg-white"
        >
          <option value="all">همه وضعیت‌ها</option>
          <option value="enabled">فعال</option>
          <option value="disabled">غیرفعال</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm">
            <thead className="bg-slate-50 text-slate-600 font-medium">
              <tr>
                <th className="py-3 px-4 border-b border-slate-200">شناسه</th>
                <th className="py-3 px-4 border-b border-slate-200">کد ملی</th>
                <th className="py-3 px-4 border-b border-slate-200">وضعیت</th>
                <th className="py-3 px-4 border-b border-slate-200">رفرش خودکار</th>
                <th className="py-3 px-4 border-b border-slate-200">کوکی</th>
                <th className="py-3 px-4 border-b border-slate-200">انقضای رمز موقت</th>
                <th className="py-3 px-4 border-b border-slate-200">آخرین استفاده</th>
                <th className="py-3 px-4 border-b border-slate-200">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center"><Loader2 className="animate-spin inline" /></td></tr>
              ) : accounts.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400">هیچ اکانتی یافت نشد</td></tr>
              ) : accounts.map((a, i) => (
                <motion.tr key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }} className="row-hover border-b border-slate-100 last:border-0" data-testid={`account-row-${a.id}`}>
                  <td className="py-3 px-4 font-mono text-slate-700">{a.id}</td>
                  <td className="py-3 px-4 tabular-nums">{faDigits(a.national_code)}</td>
                  <td className="py-3 px-4">
                    <button
                      data-testid={`toggle-enabled-${a.id}`}
                      onClick={() => toggleEnabled(a)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${a.enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}
                    >{a.enabled ? "فعال" : "غیرفعال"}</button>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      data-testid={`toggle-refresh-${a.id}`}
                      onClick={() => toggleRefresh(a)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${!a.refresh_disabled ? "bg-teal-50 text-teal-700 border border-teal-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}
                    >{!a.refresh_disabled ? "فعال" : "متوقف"}</button>
                  </td>
                  <td className="py-3 px-4">
                    {a.has_cookie ? (
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${a.cookie_healthy ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${a.cookie_healthy ? "bg-emerald-500" : "bg-rose-500"}`} />
                        {a.cookie_healthy ? "سالم" : "منقضی"}
                      </span>
                    ) : <span className="text-xs text-slate-400">-</span>}
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-600">{a.otp_expires_text || "-"}</td>
                  <td className="py-3 px-4 text-xs text-slate-500">{timeAgo(a.last_used_at)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      <button data-testid={`refresh-account-${a.id}`} onClick={() => doRefresh(a)} disabled={refreshing[a.id]} className="p-2 rounded-lg hover:bg-slate-100 text-teal-600" title="رفرش">
                        {refreshing[a.id] ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                      </button>
                      <button data-testid={`view-cookie-${a.id}`} onClick={() => showCookie(a)} className="p-2 rounded-lg hover:bg-slate-100 text-indigo-600" title="مشاهده کوکی"><Eye size={16} /></button>
                      <button data-testid={`edit-account-${a.id}`} onClick={() => setEditModal(a)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" title="ویرایش"><Pencil size={16} /></button>
                      <button data-testid={`delete-account-${a.id}`} onClick={() => doDelete(a)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" title="حذف"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {cookieModal && <CookieModal data={cookieModal} onClose={() => setCookieModal(null)} />}
      {editModal && <EditModal account={editModal} onClose={() => setEditModal(null)} onSaved={() => { setEditModal(null); load(); }} />}
    </div>
  );
}

function CookieBlock({ title, subtitle, cookie, testid, copyTestId, onAction, actionLabel, busy, showAffinity }) {
  const ok = !!cookie && !!cookie.healthy;
  const affinityNames = showAffinity && cookie?.cookie_string
    ? cookie.cookie_string.split(";").map((p) => p.trim().split("=")[0]).filter((n) => n.startsWith("ARRAffinity") || n.startsWith("TS"))
    : null;
  const status = !cookie ? "ذخیره نشده" : cookie.invalid ? "نامعتبر" : cookie.expired ? "منقضی" : "معتبر";
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="font-semibold text-sm text-slate-800">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full ${ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
          {status}
        </span>
      </div>
      {cookie ? (
        <>
          <div className="text-xs text-slate-500">
            تاریخ انقضا: <b className="text-slate-700">{new Date(cookie.expires_at * 1000).toLocaleString("fa-IR")}</b>
          </div>
          {cookie.last_error && <div className="text-xs text-rose-600">آخرین خطا: {cookie.last_error}</div>}
          {affinityNames && (
            <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
              کوکی‌های load balancer:
              {affinityNames.length ? affinityNames.map((n) => (
                <span key={n} className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{n}</span>
              )) : <span className="text-amber-600">دریافت نشد</span>}
            </div>
          )}
          <textarea readOnly value={cookie.cookie_string || ""} className="w-full h-24 p-3 rounded-xl border border-slate-200 bg-slate-50 font-mono text-xs" />
        </>
      ) : (
        <div className="text-sm text-slate-500">هنوز برای این بخش کوکی ذخیره نشده.</div>
      )}
      <div className="flex gap-2 flex-wrap">
        <button
          data-testid={copyTestId}
          disabled={!cookie?.cookie_string}
          onClick={() => copyText(cookie?.cookie_string || "", `${title} کپی شد`)}
          className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl inline-flex items-center gap-2 text-sm"
        >
          <Copy size={16} /> کپی کردن
        </button>
        {onAction && (
          <button
            data-testid={`${testid}-action`}
            onClick={onAction}
            disabled={busy}
            className="bg-slate-100 hover:bg-slate-200 disabled:opacity-60 text-slate-700 px-4 py-2 rounded-xl inline-flex items-center gap-2 text-sm"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function CookieModal({ data, onClose }) {
  const [sana, setSana] = useState(data.sana ?? null);
  const [busy, setBusy] = useState(false);

  const fetchSana = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/accounts/${data.account.id}/sana-cookie`);
      if (r.success) {
        const { data: fresh } = await api.get(`/admin/accounts/${data.account.id}/cookie`);
        setSana(fresh.sana_cookie);
        toast.success("کوکی سامانه ساخته شد");
      } else {
        toast.error(r.message || "خطا در گرفتن کوکی سامانه");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "خطا در گرفتن کوکی سامانه");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
        data-testid="cookie-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">کوکی‌های اکانت {data.account.id}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="space-y-4">
          <CookieBlock
            title="کوکی اصلی"
            subtitle="my.adliran.ir"
            cookie={data.cookie}
            testid="cookie-main"
            copyTestId="cookie-copy-button"
          />
          <CookieBlock
            title="کوکی سامانه ESana"
            subtitle="sana1.adliran.ir — از روی کوکی اصلی ساخته می‌شود و با هر رفرش اصلی تازه می‌شود"
            cookie={sana}
            showAffinity
            testid="cookie-sana"
            copyTestId="cookie-sana-copy-button"
            onAction={fetchSana}
            actionLabel="گرفتن کوکی سامانه"
            busy={busy}
          />
        </div>
      </motion.div>
    </div>
  );
}

function EditModal({ account, onClose, onSaved }) {
  const [nc, setNc] = useState(account.national_code);
  const [pw, setPw] = useState(account.password);
  const [otp, setOtp] = useState(account.otp_password);
  const [enabled, setEnabled] = useState(!!account.enabled);
  const [refreshDisabled, setRefreshDisabled] = useState(!!account.refresh_disabled);
  const [otpExpText, setOtpExpText] = useState(account.otp_expires_text || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/accounts/${account.id}`, {
        national_code: nc, password: pw, otp_password: otp,
        enabled, refresh_disabled: refreshDisabled,
        otp_expires_at: 0,
        otp_expires_text: otpExpText,
      });
      toast.success("ذخیره شد"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "خطا در ذخیره"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" data-testid="edit-account-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">ویرایش اکانت {account.id}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="space-y-3">
          <F label="کد ملی"><input data-testid="edit-national-code" value={nc} onChange={e => setNc(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-300" /></F>
          <F label="رمز عبور"><input data-testid="edit-password" value={pw} onChange={e => setPw(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-300" /></F>
          <F label="رمز موقت (OTP)"><input data-testid="edit-otp" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-300" /></F>
          <F label="تاریخ و ساعت انقضای رمز موقت (متن آزاد)">
            <input data-testid="edit-otp-exp" type="text" value={otpExpText} onChange={e => setOtpExpText(e.target.value)} placeholder="مثلاً: ۱۴۰۳/۰۵/۱۵ ساعت ۱۴:۳۰" className="w-full px-3 py-2 rounded-xl border border-slate-300" />
          </F>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><input data-testid="edit-enabled" type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> فعال</label>
            <label className="flex items-center gap-2 text-sm"><input data-testid="edit-refresh-disabled" type="checkbox" checked={refreshDisabled} onChange={e => setRefreshDisabled(e.target.checked)} /> غیرفعال کردن رفرش</label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700">انصراف</button>
          <button data-testid="edit-save-button" onClick={save} disabled={saving} className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2">{saving && <Loader2 size={16} className="animate-spin" />} ذخیره</button>
        </div>
      </motion.div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-sm font-medium text-slate-700 mb-1 block">{label}</label>{children}</div>;
}
